/**
 * What a form must contain before it is worth sending.
 *
 * These are input schemas, not response schemas: they describe what the person
 * types, and react-hook-form checks them through `zodResolver` before the
 * request is made. The server enforces the same rules again — it has to, since
 * nothing stops a caller skipping this app — but until now it was the ONLY
 * thing enforcing them, so a ten-character password rule the UI promised in
 * its hint text was only discovered on submit.
 */
import { z } from 'zod';

const email = z
	.string()
	.trim()
	.min(1, 'Enter your email.')
	.email('That does not look like an email address.');

/** A new account's email: no capital letters, and an @ (the server checks
 *  the same, `backend/src/services/accountRules.ts`). Signing in and "forgot
 *  password" keep `email`, so an older account can still be typed as it is. */
const newEmail = z
	.string()
	.trim()
	.min(1, 'Enter your email.')
	.refine((value) => value.includes('@'), 'An email address must contain @.')
	.refine(
		(value) => value === value.toLowerCase(),
		'Use small letters only in the email address.',
	)
	.pipe(z.string().email('That does not look like an email address.'));

/** What a new password is missing, in words; empty when it is acceptable.
 *  The server checks the same (`accountRules.passwordProblems`). */
function passwordProblems(password: string): string[] {
	const problems: string[] = [];
	if (password.length < 8) problems.push('at least 8 characters');
	if (!/[A-Z]/.test(password)) problems.push('a capital letter');
	if (!/[a-z]/.test(password)) problems.push('a small letter');
	if (!/[0-9]/.test(password)) problems.push('a number');
	if (!/[^A-Za-z0-9]/.test(password)) problems.push('a special character');
	return problems;
}

const newPassword = z.string().superRefine((value, context) => {
	const problems = passwordProblems(value);
	if (problems.length > 0)
		context.addIssue({
			code: 'custom',
			message: `The password needs ${problems.join(', ')}.`,
		});
});

export const loginFormSchema = z.object({
	email,
	/* Not `newPassword`: an existing password set under an older rule must still
     be typeable, and the server is the judge of whether it is right. */
	password: z.string().min(1, 'Enter your password.'),
});

export const registerFormSchema = z.object({
	firmName: z.string().trim().min(1, "Enter your firm's name."),
	name: z.string().trim().min(1, 'Enter your name.'),
	email: newEmail,
	password: newPassword,
});

export const acceptInviteFormSchema = z.object({
	name: z.string().trim().min(1, 'Enter your name.'),
	password: newPassword,
});

export const forgotPasswordFormSchema = z.object({ email });

export const resetPasswordFormSchema = z.object({ password: newPassword });

/** `POST /clients` accepts exactly these four fields — `gstin`/`erp` optional,
 *  `name`/`code` required, matching `AddClientPage`'s existing validation. */
export const addClientFormSchema = z.object({
	name: z.string().trim().min(1, "Enter the client's name."),
	code: z.string().trim().min(1, 'Enter a short code.'),
	erp: z.string().trim(),
	gstin: z.string().trim(),
});

/** An administrator creating a user (`POST /users`). */
export const createUserFormSchema = z.object({
	name: z.string().trim().min(2, 'Enter their name.'),
	email: newEmail,
	password: newPassword,
	role: z.enum(['ADMIN', 'AUDITOR', 'DATA_ENTRY']),
});

export const changePasswordFormSchema = z.object({
	currentPassword: z.string().min(1, 'Enter your current password.'),
	newPassword,
});

export type LoginForm = z.infer<typeof loginFormSchema>;
export type RegisterForm = z.infer<typeof registerFormSchema>;
export type AcceptInviteForm = z.infer<typeof acceptInviteFormSchema>;
export type ForgotPasswordForm = z.infer<typeof forgotPasswordFormSchema>;
export type ResetPasswordForm = z.infer<typeof resetPasswordFormSchema>;
export type AddClientForm = z.infer<typeof addClientFormSchema>;
export type ChangePasswordForm = z.infer<typeof changePasswordFormSchema>;
export type CreateUserForm = z.infer<typeof createUserFormSchema>;
